import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Icon,
} from 'semantic-ui-react';
import { isEqual } from 'lodash';
import { TableSortType } from '../../Chart/interfaces';
import {
  Order,
  RelationalOperatorKey,
} from '../../../utils/apollo/graphqlSchema';
import { SvgIcon } from '../../Icon';
import { PrimaryButton, SecondaryButton } from '../../Button';
import ColumnFilterConditionSelect from './ColumnFilterConditionSelect';
import {
  ColumnFilterConfig,
  ColumnFilterType,
  ColumnSortConfig,
} from './types';
import FilterValues from './FilterValues';
import { StyledPopover } from '../../Popover/PopoverUI';
import * as ColumnFilterWidgetUI from './ColumnFilterWidgetUI';
import {
  FloatingLayerProvider,
  getFloatingHostElement,
} from '../../FloatingManager';
import ThinDivider from '../../ThinDivider';

interface IProps {
  anchorEl: React.MutableRefObject<HTMLElement | null>;
  isOpen: boolean;
  sortConfig: ColumnSortConfig | null;
  filterConfig: ColumnFilterConfig | null;
  onClose: () => void;
  onChangeSort: (sortConfig: ColumnSortConfig | null) => void;
  onChangeFilter: (filterConfig: ColumnFilterConfig | null) => void;
  columnValues: (string | number)[];
}

const ColumnFilterWidget = ({
  anchorEl,
  isOpen,
  sortConfig,
  filterConfig,
  onClose,
  onChangeSort,
  onChangeFilter,
  columnValues,
}: IProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [draftSortConfig, setDraftSortConfig] =
    useState<ColumnSortConfig | null>(null);
  const [draftFilterConfig, setDraftFilterConfig] =
    useState<ColumnFilterConfig | null>(null);

  // based on props, open the associated accordion that is being used
  useEffect(() => {
    if (filterConfig) {
      if (filterConfig.type === ColumnFilterType.Condition) {
        setActiveIndex(2);
      } else if (filterConfig.type === ColumnFilterType.Value) {
        setActiveIndex(3);
      }
      setDraftFilterConfig(filterConfig);
    } else if (sortConfig) {
      if (sortConfig.sortType === TableSortType.ALPHABETICAL) {
        setActiveIndex(0);
      } else if (sortConfig.sortType === TableSortType.NUMERICAL) {
        setActiveIndex(1);
      }
      setDraftSortConfig(sortConfig);
    }
  }, [filterConfig, sortConfig]);

  const handleClick = (index: number) => {
    onReset();
    if (isEqual(index, activeIndex)) {
      setActiveIndex(null);
    } else {
      setActiveIndex(index);
    }
  };

  const handleChangeSort = (sortType: TableSortType, sortOrder: Order) => {
    if (draftFilterConfig) {
      setDraftFilterConfig(null);
    }
    setDraftSortConfig({
      sortType,
      sortOrder,
    });
  };

  const handleChangeFilter = (
    condition: RelationalOperatorKey,
    value?: string,
  ) => {
    if (draftSortConfig) {
      setDraftSortConfig(null);
    }
    const values = value?.split(',');
    setDraftFilterConfig({
      condition,
      values,
      type: ColumnFilterType.Condition,
    });
  };

  const onUpdateFilterValues = (value: string | number) => {
    if (draftSortConfig) {
      setDraftSortConfig(null);
    }

    let values = draftFilterConfig?.values ?? [];
    if (values.includes(value)) {
      values = values.filter((v) => v !== value);
    } else {
      values = [...values, value];
    }

    setDraftFilterConfig({
      condition: RelationalOperatorKey.Equal,
      values,
      type: ColumnFilterType.Value,
    });
  };

  const isSelectedAccordion = (sortType: TableSortType, sortOrder: Order) => {
    if (draftSortConfig) {
      return (
        sortType === draftSortConfig.sortType &&
        sortOrder === draftSortConfig.sortOrder
      );
    }

    return false;
  };

  const onApply = () => {
    if (!isEqual(filterConfig, draftFilterConfig)) {
      if (draftFilterConfig?.values?.length) {
        onChangeFilter(draftFilterConfig);
      } else {
        onChangeFilter(null);
      }
    }
    if (!isEqual(sortConfig, draftSortConfig)) {
      onChangeSort(draftSortConfig);
    }
    onClose();
  };

  const onReset = () => {
    setActiveIndex(null);
    setDraftFilterConfig(null);
    setDraftSortConfig(null);
  };

  return (
    <FloatingLayerProvider>
      <StyledPopover
        anchorEl={anchorEl.current}
        open={isOpen}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        container={getFloatingHostElement()}
      >
        <ColumnFilterWidgetUI.Wrapper>
          <Accordion>
            <AccordionTitle
              active={activeIndex === 0}
              index={0}
              onClick={() => handleClick(0)}
            >
              <Icon name="dropdown" />
              Sort Alphabetically
            </AccordionTitle>
            <ColumnFilterWidgetUI.StyledAccordionContent
              active={activeIndex === 0}
            >
              <ColumnFilterWidgetUI.AccordionRow>
                {isSelectedAccordion(TableSortType.ALPHABETICAL, Order.Asc) && (
                  <SvgIcon name="checkmark" width={12} height={12} />
                )}
                <ColumnFilterWidgetUI.AccordionItem
                  onClick={() =>
                    handleChangeSort(TableSortType.ALPHABETICAL, Order.Asc)
                  }
                  $isActive={isSelectedAccordion(
                    TableSortType.ALPHABETICAL,
                    Order.Asc,
                  )}
                >
                  Sort A to Z
                </ColumnFilterWidgetUI.AccordionItem>
              </ColumnFilterWidgetUI.AccordionRow>
              <ColumnFilterWidgetUI.AccordionRow>
                {isSelectedAccordion(
                  TableSortType.ALPHABETICAL,
                  Order.Desc,
                ) && <SvgIcon name="checkmark" width={12} height={12} />}
                <ColumnFilterWidgetUI.AccordionItem
                  onClick={() =>
                    handleChangeSort(TableSortType.ALPHABETICAL, Order.Desc)
                  }
                  $isActive={isSelectedAccordion(
                    TableSortType.ALPHABETICAL,
                    Order.Desc,
                  )}
                >
                  Sort Z to A
                </ColumnFilterWidgetUI.AccordionItem>
              </ColumnFilterWidgetUI.AccordionRow>
            </ColumnFilterWidgetUI.StyledAccordionContent>

            <AccordionTitle
              active={activeIndex === 1}
              index={1}
              onClick={() => handleClick(1)}
            >
              <Icon name="dropdown" />
              Sort Numerically
            </AccordionTitle>
            <ColumnFilterWidgetUI.StyledAccordionContent
              active={activeIndex === 1}
            >
              <ColumnFilterWidgetUI.AccordionRow>
                {isSelectedAccordion(TableSortType.NUMERICAL, Order.Asc) && (
                  <SvgIcon name="checkmark" width={12} height={12} />
                )}
                <ColumnFilterWidgetUI.AccordionItem
                  onClick={() =>
                    handleChangeSort(TableSortType.NUMERICAL, Order.Asc)
                  }
                  $isActive={isSelectedAccordion(
                    TableSortType.NUMERICAL,
                    Order.Asc,
                  )}
                >
                  Sort 1 to 10
                </ColumnFilterWidgetUI.AccordionItem>
              </ColumnFilterWidgetUI.AccordionRow>
              <ColumnFilterWidgetUI.AccordionRow>
                {isSelectedAccordion(TableSortType.NUMERICAL, Order.Desc) && (
                  <SvgIcon name="checkmark" width={8} height={8} />
                )}
                <ColumnFilterWidgetUI.AccordionItem
                  onClick={() =>
                    handleChangeSort(TableSortType.NUMERICAL, Order.Desc)
                  }
                  $isActive={isSelectedAccordion(
                    TableSortType.NUMERICAL,
                    Order.Desc,
                  )}
                >
                  Sort 10 to 1
                </ColumnFilterWidgetUI.AccordionItem>
              </ColumnFilterWidgetUI.AccordionRow>
            </ColumnFilterWidgetUI.StyledAccordionContent>

            <ThinDivider layout="horizontal" size="full" />

            <AccordionTitle
              active={activeIndex === 2}
              index={2}
              onClick={() => handleClick(2)}
            >
              <Icon name="dropdown" />
              Filter by condition
            </AccordionTitle>
            <AccordionContent active={activeIndex === 2}>
              <ColumnFilterWidgetUI.AccordionRow>
                <ColumnFilterConditionSelect
                  filterCondition={draftFilterConfig?.condition}
                  onChange={(condition) => handleChangeFilter(condition)}
                />
              </ColumnFilterWidgetUI.AccordionRow>
              {draftFilterConfig?.condition && (
                <ColumnFilterWidgetUI.AccordionRow>
                  <ColumnFilterWidgetUI.StyledInputField
                    value={draftFilterConfig?.values?.join(',')}
                    onChange={(value) =>
                      handleChangeFilter(draftFilterConfig.condition, value)
                    }
                  />
                </ColumnFilterWidgetUI.AccordionRow>
              )}
            </AccordionContent>

            <AccordionTitle
              active={activeIndex === 3}
              index={3}
              onClick={() => handleClick(3)}
            >
              <Icon name="dropdown" />
              Filter by values
            </AccordionTitle>
            <AccordionContent active={activeIndex === 3}>
              <FilterValues
                filterConfig={draftFilterConfig}
                columnValues={columnValues}
                onChange={onUpdateFilterValues}
              />
            </AccordionContent>
          </Accordion>
          <ColumnFilterWidgetUI.Footer>
            <ColumnFilterWidgetUI.ActionItem onClick={onReset}>
              Reset
            </ColumnFilterWidgetUI.ActionItem>
            <ColumnFilterWidgetUI.ActionsContainer>
              <SecondaryButton
                type="button"
                onClick={onClose}
                size="small"
                trackContext="column filter"
                trackAction="cancel"
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={onApply}
                size="small"
                trackContext="column filter"
                trackAction="confirm"
              >
                Confirm
              </PrimaryButton>
            </ColumnFilterWidgetUI.ActionsContainer>
          </ColumnFilterWidgetUI.Footer>
        </ColumnFilterWidgetUI.Wrapper>
      </StyledPopover>
    </FloatingLayerProvider>
  );
};

export default ColumnFilterWidget;
